import React, { useState, useEffect } from 'react'
import dayjs from 'dayjs'

function buildCalendarDays(viewDate, startDate, endDate, anchor) {
    const startOfMonth = viewDate.startOf('month')
    const endOfMonth = viewDate.endOf('month')
    const startOfCalendar = startOfMonth.startOf('week')
    const endOfCalendar = endOfMonth.endOf('week')

    const lo = startDate.startOf('day')
    const hi = endDate.startOf('day')
    const rangeLow = lo.isBefore(hi) ? lo : hi
    const rangeHigh = lo.isBefore(hi) ? hi : lo
    const hasPending = anchor !== null

    const days = []
    let current = startOfCalendar

    while (current.isBefore(endOfCalendar, 'day') || current.isSame(endOfCalendar, 'day')) {
        const d = current.startOf('day')
        const isPendingAnchor = hasPending && d.isSame(anchor, 'day')
        const inCommittedRange = !hasPending && !d.isBefore(rangeLow) && !d.isAfter(rangeHigh)
        const isRangeStart = !hasPending && d.isSame(rangeLow, 'day')
        const isRangeEnd = !hasPending && d.isSame(rangeHigh, 'day')
        const isInRangeMiddle = inCommittedRange && !isRangeStart && !isRangeEnd

        days.push({
            date: current,
            isCurrentMonth: current.isSame(viewDate, 'month'),
            isToday: current.isSame(dayjs(), 'day'),
            isPendingAnchor,
            isRangeEdge: isRangeStart || isRangeEnd,
            isInRangeMiddle
        })
        current = current.add(1, 'day')
    }

    return days
}

function dayCellStyle(day) {
    const { isPendingAnchor, isRangeEdge, isInRangeMiddle, isToday, isCurrentMonth } = day
    let background = 'transparent'
    let color = 'var(--text-light)'
    let fontWeight = '400'

    if (isPendingAnchor || isRangeEdge) {
        background = 'var(--primary-orange)'
        color = 'var(--white)'
        fontWeight = '600'
    } else if (isInRangeMiddle) {
        background = 'rgba(255, 152, 0, 0.22)'
        color = 'var(--text-light)'
        fontWeight = '500'
    } else if (isToday) {
        background = 'var(--dark-gray)'
        color = 'var(--primary-orange)'
        fontWeight = '600'
    }

    if (!isCurrentMonth && !isPendingAnchor && !isRangeEdge && !isInRangeMiddle) {
        color = 'var(--text-muted)'
    }

    return {
        padding: '8px 4px',
        border: 'none',
        borderRadius: 'var(--radius-sm)',
        fontSize: 'var(--text-sm)',
        cursor: 'pointer',
        background,
        color,
        fontWeight,
        transition: 'var(--transition-normal)'
    }
}

function mobileDayCellStyle(day) {
    const base = dayCellStyle(day)
    return {
        ...base,
        padding: '6px 2px',
        fontSize: 'var(--text-xs)'
    }
}

/** Десктоп: одно поле, выпадающий календарь — первый клик «от», второй «до».
 *  getResetRange — опционально: диапазон для Clear; иначе последние 7 дней (History). */
export function HistoryDesktopDateRangePicker({ label, startDate, endDate, onRangeChange, getResetRange }) {
    const [isOpen, setIsOpen] = useState(false)
    const [viewDate, setViewDate] = useState(startDate)
    const [anchor, setAnchor] = useState(null)

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isOpen && !event.target.closest('.history-date-range-desktop')) {
                setIsOpen(false)
            }
        }
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [isOpen])

    const toggleOpen = () => {
        setIsOpen((prev) => {
            const next = !prev
            if (next) {
                setViewDate(startDate)
                setAnchor(null)
            }
            return next
        })
    }

    const formatRange = () => `${startDate.format('MM/DD/YYYY')} — ${endDate.format('MM/DD/YYYY')}`

    const handleDayClick = (d) => {
        const picked = d.startOf('day')
        if (anchor === null) {
            setAnchor(picked)
            return
        }
        const lo = anchor.isBefore(picked) ? anchor : picked
        const hi = anchor.isBefore(picked) ? picked : anchor
        onRangeChange(lo, hi)
        setAnchor(null)
        setIsOpen(false)
    }

    const handleClear = () => {
        if (typeof getResetRange === 'function') {
            const { start, end } = getResetRange()
            onRangeChange(start, end)
        } else {
            onRangeChange(dayjs().subtract(7, 'day'), dayjs())
        }
        setAnchor(null)
        setIsOpen(false)
    }

    const handleToday = () => {
        const t = dayjs().startOf('day')
        onRangeChange(t, t)
        setAnchor(null)
        setIsOpen(false)
    }

    const navigateMonth = (direction) => {
        setViewDate((v) => (direction === 'prev' ? v.subtract(1, 'month') : v.add(1, 'month')))
    }

    const navigateYear = (direction) => {
        setViewDate((v) => (direction === 'prev' ? v.subtract(1, 'year') : v.add(1, 'year')))
    }

    const navBtn = () => ({
        background: 'none',
        border: '1px solid var(--light-gray)',
        borderRadius: 'var(--radius-sm)',
        color: 'var(--text-light)',
        padding: '4px 6px',
        cursor: 'pointer',
        fontSize: 'var(--text-xs)',
        minWidth: '24px',
        height: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    })

    return (
        <div
            className="form-group history-date-range-desktop"
            style={{ minWidth: '260px', flex: '0 1 280px', position: 'relative' }}
        >
            <label className="form-label">{label}</label>
            <div
                className="form-input"
                onClick={toggleOpen}
                style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px'
                }}
            >
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {formatRange()}
                </span>
                <span style={{ color: 'var(--primary-orange)', flexShrink: 0 }}>📅</span>
            </div>

            {isOpen && (
                <div
                    style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        zIndex: 1000,
                        background: 'var(--medium-gray)',
                        border: '1px solid var(--light-gray)',
                        borderRadius: 'var(--radius-lg)',
                        padding: 'var(--spacing-md)',
                        boxShadow: 'var(--shadow-card)',
                        marginTop: '4px',
                        width: '280px',
                        minWidth: '280px'
                    }}
                >
                    <div
                        style={{
                            fontSize: 'var(--text-xs)',
                            color: anchor ? 'var(--primary-orange)' : 'var(--text-muted)',
                            marginBottom: 'var(--spacing-sm)',
                            textAlign: 'center'
                        }}
                    >
                        {anchor ? 'Second click: end date' : 'First click: start date'}
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: 'var(--spacing-sm)',
                            gap: '4px'
                        }}
                    >
                        <button
                            type="button"
                            onClick={() => navigateYear('prev')}
                            style={navBtn()}
                            onMouseEnter={(e) => { e.target.style.borderColor = 'var(--primary-orange)' }}
                            onMouseLeave={(e) => { e.target.style.borderColor = 'var(--light-gray)' }}
                        >
                            ⟪
                        </button>
                        <button
                            type="button"
                            onClick={() => navigateMonth('prev')}
                            style={navBtn()}
                            onMouseEnter={(e) => { e.target.style.borderColor = 'var(--primary-orange)' }}
                            onMouseLeave={(e) => { e.target.style.borderColor = 'var(--light-gray)' }}
                        >
                            ‹
                        </button>
                        <span
                            style={{
                                fontSize: 'var(--text-sm)',
                                fontWeight: '600',
                                color: 'var(--text-light)',
                                flex: 1,
                                textAlign: 'center'
                            }}
                        >
                            {viewDate.format('MMMM YYYY')}
                        </span>
                        <button
                            type="button"
                            onClick={() => navigateMonth('next')}
                            style={navBtn()}
                            onMouseEnter={(e) => { e.target.style.borderColor = 'var(--primary-orange)' }}
                            onMouseLeave={(e) => { e.target.style.borderColor = 'var(--light-gray)' }}
                        >
                            ›
                        </button>
                        <button
                            type="button"
                            onClick={() => navigateYear('next')}
                            style={navBtn()}
                            onMouseEnter={(e) => { e.target.style.borderColor = 'var(--primary-orange)' }}
                            onMouseLeave={(e) => { e.target.style.borderColor = 'var(--light-gray)' }}
                        >
                            ⟫
                        </button>
                    </div>

                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(7, 1fr)',
                            gap: '2px',
                            marginBottom: 'var(--spacing-sm)'
                        }}
                    >
                        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((dow) => (
                            <div
                                key={dow}
                                style={{
                                    padding: '8px 4px',
                                    textAlign: 'center',
                                    fontSize: 'var(--text-xs)',
                                    fontWeight: '600',
                                    color: 'var(--text-muted)'
                                }}
                            >
                                {dow}
                            </div>
                        ))}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
                        {buildCalendarDays(viewDate, startDate, endDate, anchor).map((day, index) => {
                            const base = dayCellStyle(day)
                            return (
                                <button
                                    type="button"
                                    key={index}
                                    onClick={() => handleDayClick(day.date)}
                                    style={base}
                                    onMouseEnter={(e) => {
                                        if (!day.isPendingAnchor && !day.isRangeEdge && !day.isInRangeMiddle) {
                                            e.target.style.backgroundColor = 'var(--dark-gray)'
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.background = base.background
                                    }}
                                >
                                    {day.date.format('D')}
                                </button>
                            )
                        })}
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginTop: 'var(--spacing-sm)',
                            paddingTop: 'var(--spacing-sm)',
                            borderTop: '1px solid var(--light-gray)',
                            gap: 'var(--spacing-sm)'
                        }}
                    >
                        <button
                            type="button"
                            onClick={handleClear}
                            style={{
                                background: 'none',
                                border: '1px solid var(--light-gray)',
                                borderRadius: 'var(--radius-sm)',
                                color: 'var(--text-light)',
                                padding: '6px 12px',
                                cursor: 'pointer',
                                fontSize: 'var(--text-xs)',
                                flex: 1,
                                transition: 'var(--transition-normal)'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.borderColor = 'var(--primary-orange)'
                                e.target.style.color = 'var(--primary-orange)'
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.borderColor = 'var(--light-gray)'
                                e.target.style.color = 'var(--text-light)'
                            }}
                        >
                            Clear
                        </button>
                        <button
                            type="button"
                            onClick={handleToday}
                            style={{
                                background: 'var(--primary-orange)',
                                border: '1px solid var(--primary-orange)',
                                borderRadius: 'var(--radius-sm)',
                                color: 'var(--white)',
                                padding: '6px 12px',
                                cursor: 'pointer',
                                fontSize: 'var(--text-xs)',
                                flex: 1,
                                transition: 'var(--transition-normal)'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = 'var(--dark-orange)'
                                e.target.style.borderColor = 'var(--dark-orange)'
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'var(--primary-orange)'
                                e.target.style.borderColor = 'var(--primary-orange)'
                            }}
                        >
                            Today
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

/** Мобильный: то же поведение, календарь в модальном окне. */
export function HistoryMobileDateRangePicker({ label, startDate, endDate, onRangeChange, getResetRange }) {
    const [isOpen, setIsOpen] = useState(false)
    const [viewDate, setViewDate] = useState(startDate)
    const [anchor, setAnchor] = useState(null)

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isOpen && !event.target.closest('.history-date-range-modal')) {
                setIsOpen(false)
            }
        }
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [isOpen])

    const toggleOpen = () => {
        setIsOpen((prev) => {
            const next = !prev
            if (next) {
                setViewDate(startDate)
                setAnchor(null)
            }
            return next
        })
    }

    const formatRange = () => `${startDate.format('MM/DD/YYYY')} — ${endDate.format('MM/DD/YYYY')}`

    const handleDayClick = (d) => {
        const picked = d.startOf('day')
        if (anchor === null) {
            setAnchor(picked)
            return
        }
        const lo = anchor.isBefore(picked) ? anchor : picked
        const hi = anchor.isBefore(picked) ? picked : anchor
        onRangeChange(lo, hi)
        setAnchor(null)
        setIsOpen(false)
    }

    const handleClear = () => {
        if (typeof getResetRange === 'function') {
            const { start, end } = getResetRange()
            onRangeChange(start, end)
        } else {
            onRangeChange(dayjs().subtract(7, 'day'), dayjs())
        }
        setAnchor(null)
        setIsOpen(false)
    }

    const handleToday = () => {
        const t = dayjs().startOf('day')
        onRangeChange(t, t)
        setAnchor(null)
        setIsOpen(false)
    }

    const navigateMonth = (direction) => {
        setViewDate((v) => (direction === 'prev' ? v.subtract(1, 'month') : v.add(1, 'month')))
    }

    const navigateYear = (direction) => {
        setViewDate((v) => (direction === 'prev' ? v.subtract(1, 'year') : v.add(1, 'year')))
    }

    const navBtn = () => ({
        background: 'none',
        border: '1px solid var(--light-gray)',
        borderRadius: 'var(--radius-sm)',
        color: 'var(--text-light)',
        padding: '4px 6px',
        cursor: 'pointer',
        fontSize: 'var(--text-xs)',
        minWidth: '24px',
        height: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    })

    return (
        <div className="mobile-filter-group history-date-range-mobile" style={{ position: 'relative' }}>
            <label className="mobile-filter-label">{label}</label>
            <div
                className="mobile-filter-input"
                onClick={toggleOpen}
                style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '6px'
                }}
            >
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {formatRange()}
                </span>
                <span style={{ color: 'var(--primary-orange)', flexShrink: 0 }}>📅</span>
            </div>

            {isOpen && (
                <>
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'rgba(0, 0, 0, 0.5)',
                            zIndex: 9999
                        }}
                        onClick={() => setIsOpen(false)}
                        aria-hidden
                    />
                    <div
                        className="history-date-range-modal"
                        style={{
                            position: 'fixed',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 10000,
                            background: 'var(--medium-gray)',
                            border: '1px solid var(--light-gray)',
                            borderRadius: 'var(--radius-lg)',
                            padding: 'var(--spacing-md)',
                            boxShadow: 'var(--shadow-card)',
                            width: '90vw',
                            maxWidth: '320px',
                            maxHeight: '80vh',
                            overflow: 'auto'
                        }}
                    >
                        <div
                            style={{
                                fontSize: 'var(--text-xs)',
                                color: anchor ? 'var(--primary-orange)' : 'var(--text-muted)',
                                marginBottom: 'var(--spacing-sm)',
                                textAlign: 'center'
                            }}
                        >
                            {anchor ? 'Second click: end date' : 'First click: start date'}
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginBottom: 'var(--spacing-sm)',
                                gap: '4px'
                            }}
                        >
                            <button
                                type="button"
                                onClick={() => navigateYear('prev')}
                                style={navBtn()}
                                onMouseEnter={(e) => { e.target.style.borderColor = 'var(--primary-orange)' }}
                                onMouseLeave={(e) => { e.target.style.borderColor = 'var(--light-gray)' }}
                            >
                                ⟪
                            </button>
                            <button
                                type="button"
                                onClick={() => navigateMonth('prev')}
                                style={navBtn()}
                                onMouseEnter={(e) => { e.target.style.borderColor = 'var(--primary-orange)' }}
                                onMouseLeave={(e) => { e.target.style.borderColor = 'var(--light-gray)' }}
                            >
                                ‹
                            </button>
                            <span
                                style={{
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: '600',
                                    color: 'var(--text-light)',
                                    flex: 1,
                                    textAlign: 'center'
                                }}
                            >
                                {viewDate.format('MMMM YYYY')}
                            </span>
                            <button
                                type="button"
                                onClick={() => navigateMonth('next')}
                                style={navBtn()}
                                onMouseEnter={(e) => { e.target.style.borderColor = 'var(--primary-orange)' }}
                                onMouseLeave={(e) => { e.target.style.borderColor = 'var(--light-gray)' }}
                            >
                                ›
                            </button>
                            <button
                                type="button"
                                onClick={() => navigateYear('next')}
                                style={navBtn()}
                                onMouseEnter={(e) => { e.target.style.borderColor = 'var(--primary-orange)' }}
                                onMouseLeave={(e) => { e.target.style.borderColor = 'var(--light-gray)' }}
                            >
                                ⟫
                            </button>
                        </div>

                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(7, 1fr)',
                                gap: '2px',
                                marginBottom: 'var(--spacing-sm)'
                            }}
                        >
                            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((dow) => (
                                <div
                                    key={dow}
                                    style={{
                                        padding: '6px 2px',
                                        textAlign: 'center',
                                        fontSize: 'var(--text-xs)',
                                        fontWeight: '600',
                                        color: 'var(--text-muted)'
                                    }}
                                >
                                    {dow}
                                </div>
                            ))}
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
                            {buildCalendarDays(viewDate, startDate, endDate, anchor).map((day, index) => {
                                const base = mobileDayCellStyle(day)
                                return (
                                    <button
                                        type="button"
                                        key={index}
                                        onClick={() => handleDayClick(day.date)}
                                        style={base}
                                        onMouseEnter={(e) => {
                                            if (!day.isPendingAnchor && !day.isRangeEdge && !day.isInRangeMiddle) {
                                                e.target.style.backgroundColor = 'var(--dark-gray)'
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.background = base.background
                                        }}
                                    >
                                        {day.date.format('D')}
                                    </button>
                                )
                            })}
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                marginTop: 'var(--spacing-sm)',
                                paddingTop: 'var(--spacing-sm)',
                                borderTop: '1px solid var(--light-gray)',
                                gap: 'var(--spacing-sm)'
                            }}
                        >
                            <button
                                type="button"
                                onClick={handleClear}
                                style={{
                                    background: 'none',
                                    border: '1px solid var(--light-gray)',
                                    borderRadius: 'var(--radius-sm)',
                                    color: 'var(--text-light)',
                                    padding: '6px 12px',
                                    cursor: 'pointer',
                                    fontSize: 'var(--text-xs)',
                                    flex: 1,
                                    transition: 'var(--transition-normal)'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.borderColor = 'var(--primary-orange)'
                                    e.target.style.color = 'var(--primary-orange)'
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.borderColor = 'var(--light-gray)'
                                    e.target.style.color = 'var(--text-light)'
                                }}
                            >
                                Clear
                            </button>
                            <button
                                type="button"
                                onClick={handleToday}
                                style={{
                                    background: 'var(--primary-orange)',
                                    border: '1px solid var(--primary-orange)',
                                    borderRadius: 'var(--radius-sm)',
                                    color: 'var(--white)',
                                    padding: '6px 12px',
                                    cursor: 'pointer',
                                    fontSize: 'var(--text-xs)',
                                    flex: 1,
                                    transition: 'var(--transition-normal)'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = 'var(--dark-orange)'
                                    e.target.style.borderColor = 'var(--dark-orange)'
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'var(--primary-orange)'
                                    e.target.style.borderColor = 'var(--primary-orange)'
                                }}
                            >
                                Today
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}
